import { useQuery } from "@tanstack/react-query";
import axiosInstance from "@lib/axios";

const BASE = () => process.env.NEXT_PUBLIC_SHOP_CONNECTOR_V2_BASE_URL;

export type ChannelMetric = "revenue" | "orders" | "aov";

export type ChannelDataPoint = {
  date: string;
  revenue: number;
  orders: number;
  average_order_value: number;
};

export type ChannelSeries = {
  channel: string;
  data: ChannelDataPoint[];
};

const TRACIFY_SOURCE_LABELS: Record<string, string> = {
  facebook: "Meta",
  google: "Google",
  tiktok: "TikTok",
  pinterest: "Pinterest",
  msads: "Microsoft Ads",
  snapchat: "Snapchat",
  taboola: "Taboola",
  outbrain: "Outbrain",
  criteo: "Criteo",
  influencer: "Influencer",
  email: "Email",
  whatsapp: "WhatsApp",
  referred: "Organic",
  direct: "Direct",
};

export function isTracifySource(channel: string): boolean {
  return channel in TRACIFY_SOURCE_LABELS;
}

export function formatChannelLabel(channel: string): string {
  if (TRACIFY_SOURCE_LABELS[channel]) {
    return TRACIFY_SOURCE_LABELS[channel];
  }
  return channel.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());
}

export type ChannelOverviewResponse = {
  series: ChannelSeries[];
};

const KNOWN_CHANNEL_COLORS: Record<string, string> = {
  Meta: "#3b82f6",
  Google: "#ef4444",
  Influencer: "#a855f7",
  Email: "#06b6d4",
};

const FALLBACK_COLORS = [
  "#3b82f6",
  "#ef4444",
  "#a855f7",
  "#06b6d4",
  "#f59e0b",
  "#10b981",
  "#ec4899",
  "#14b8a6",
  "#f97316",
  "#6366f1",
  "#84cc16",
  "#e11d48",
  "#0ea5e9",
  "#d946ef",
  "#f43f5e",
  "#22c55e",
];

export function getChannelColor(
  channel: string,
  fallbackIndex: number
): string {
  return (
    KNOWN_CHANNEL_COLORS[channel] ??
    FALLBACK_COLORS[fallbackIndex % FALLBACK_COLORS.length]
  );
}

export function useDecifyChannelOverview(
  csid: string | undefined,
  dateFrom: string,
  dateTo: string
) {
  return useQuery<ChannelOverviewResponse>({
    queryKey: ["decify-channel-overview", csid, dateFrom, dateTo],
    queryFn: async () => {
      const res = await axiosInstance.get(
        `${BASE()}/decify/reports/${csid}/channel-overview/`,
        { params: { date_from: dateFrom, date_to: dateTo } }
      );
      return res.data;
    },
    enabled: !!csid && !!dateFrom && !!dateTo,
    staleTime: 30_000,
  });
}
